import { makeAutoObservable, runInAction } from 'mobx';

/**
 * Who a saved view setting belongs to, least to most specific. A more
 * specific scope overrides a less specific one, field by field:
 *
 *   Emfit default (code) < organization < user < user + device
 *
 * A user with no organization is their own organization.
 */
export type ViewScope =
  | { kind: 'organization'; organizationId: string }
  | { kind: 'user'; userId: string }
  | { kind: 'device'; userId: string; deviceId: string };

export type ViewScopeKind = ViewScope['kind'];

/**
 * Storage for view settings. The browser implementation stands in until
 * qs-api / qs-status store them (see docs/groups-and-organizations.md);
 * an API implementation replaces it without touching stores or views.
 */
export interface ViewSettingsRepository {
  load(view: string, scope: ViewScope): Promise<Record<string, unknown> | null>;
  save(view: string, scope: ViewScope, settings: Record<string, unknown>): Promise<void>;
  clear(view: string, scope: ViewScope): Promise<void>;
}

export function scopeKey(scope: ViewScope): string {
  switch (scope.kind) {
    case 'organization':
      return `organization:${scope.organizationId}`;
    case 'user':
      return `user:${scope.userId}`;
    case 'device':
      return `device:${scope.userId}:${scope.deviceId}`;
  }
}

export function createBrowserViewSettingsRepository(
  namespace: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = window.localStorage
): ViewSettingsRepository {
  const key = (view: string, scope: ViewScope) =>
    `${namespace}:view-settings:${view}:${scopeKey(scope)}`;
  return {
    async load(view, scope) {
      const raw = storage.getItem(key(view, scope));
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : null;
      } catch {
        return null;
      }
    },
    async save(view, scope, settings) {
      storage.setItem(key(view, scope), JSON.stringify(settings));
    },
    async clear(view, scope) {
      storage.removeItem(key(view, scope));
    },
  };
}

/**
 * Layers settings over the defaults field by field: each field takes the
 * value from the most specific layer that sets it. Fields the defaults do not
 * have, and values of the wrong type, are ignored, so a stale or tampered
 * entry cannot break a view.
 */
export function resolveViewSettings<T extends object>(
  defaults: T,
  layers: ReadonlyArray<Record<string, unknown> | null | undefined>
): { settings: T; sources: Partial<Record<keyof T, number>> } {
  const settings = { ...defaults };
  const sources: Partial<Record<keyof T, number>> = {};
  layers.forEach((layer, index) => {
    if (!layer) return;
    (Object.keys(defaults) as Array<keyof T>).forEach((field) => {
      const value = layer[field as string];
      if (value === undefined) return;
      const expected = defaults[field];
      const sameType = Array.isArray(expected)
        ? Array.isArray(value)
        : typeof value === typeof expected;
      if (!sameType) return;
      settings[field] = value as T[keyof T];
      sources[field] = index;
    });
  });
  return { settings, sources };
}

/**
 * A chosen, ordered subset of the items a view offers (charts, panes,
 * columns). `known` lists every item that existed when it was saved, so an
 * item added to the app later is shown instead of being read as hidden.
 */
export type ItemSelection = { visible: string[]; known: string[] };

export function resolveItems(available: readonly string[], selection: ItemSelection | undefined): string[] {
  if (!selection) return [...available];
  const offered = new Set(available);
  const known = new Set(selection.known);
  return [
    ...selection.visible.filter((id) => offered.has(id)),
    ...available.filter((id) => !known.has(id) && !selection.visible.includes(id)),
  ];
}

export function selectItems(available: readonly string[], visible: readonly string[]): ItemSelection {
  return { visible: [...visible], known: [...available] };
}

export type ViewContext = {
  userId: string | null;
  /** Organization defaults apply only when one is given */
  organizationId?: string | null;
  deviceId?: string | null;
};

type Options<T extends object> = {
  view: string;
  defaults: T;
  repository: ViewSettingsRepository;
  context: () => ViewContext;
};

/**
 * Settings for one configurable view, resolved from the layers that apply to
 * the current user and device. Editing works on a draft; nothing is stored
 * until it is saved for one of the scopes the user may write.
 */
export function createViewSettingsStore<T extends object>(options: Options<T>) {
  const { view, defaults, repository, context } = options;

  const store = {
    resolved: { ...defaults } as T,
    /** Index into scopesInOrder() of the layer each field came from */
    sources: {} as Partial<Record<keyof T, number>>,
    layerKinds: [] as ViewScopeKind[],
    draft: null as T | null,
    loadToken: 0,

    get settings(): T {
      return store.draft ?? store.resolved;
    },
    get isEditing() {
      return store.draft !== null;
    },
    /** The most specific scope that set any field, or 'emfit' */
    get source(): ViewScopeKind | 'emfit' {
      const indexes = Object.values(store.sources) as number[];
      if (!indexes.length) return 'emfit';
      return store.layerKinds[Math.max(...indexes)] ?? 'emfit';
    },

    scopesInOrder(): ViewScope[] {
      const { userId, organizationId, deviceId } = context();
      const scopes: ViewScope[] = [];
      if (organizationId) scopes.push({ kind: 'organization', organizationId });
      if (userId) {
        scopes.push({ kind: 'user', userId });
        if (deviceId) scopes.push({ kind: 'device', userId, deviceId });
      }
      return scopes;
    },

    scopeFor(kind: ViewScopeKind): ViewScope | null {
      return store.scopesInOrder().find((scope) => scope.kind === kind) ?? null;
    },

    async load() {
      const token = ++store.loadToken;
      const scopes = store.scopesInOrder();
      const layers = await Promise.all(
        scopes.map((scope) => repository.load(view, scope).catch(() => null))
      );
      if (token !== store.loadToken) return;
      const { settings, sources } = resolveViewSettings(defaults, layers);
      runInAction(() => {
        store.resolved = settings;
        store.sources = sources;
        store.layerKinds = scopes.map((scope) => scope.kind);
        store.draft = null;
      });
    },

    startEditing() {
      store.draft = { ...store.resolved };
    },
    update(patch: Partial<T>) {
      store.draft = { ...(store.draft ?? store.resolved), ...patch };
    },
    cancelEditing() {
      store.draft = null;
    },

    /**
     * What the view would show without `kind` and the scopes more specific
     * than it: the starting point for "Reset" while saving at that scope.
     */
    async inherited(kind: ViewScopeKind): Promise<T> {
      const scopes = store.scopesInOrder();
      const cut = scopes.findIndex((scope) => scope.kind === kind);
      const above = cut < 0 ? scopes : scopes.slice(0, cut);
      const layers = await Promise.all(
        above.map((scope) => repository.load(view, scope).catch(() => null))
      );
      return resolveViewSettings(defaults, layers).settings;
    },

    async resetDraft(kind: ViewScopeKind) {
      const settings = await store.inherited(kind);
      runInAction(() => {
        store.draft = settings;
      });
    },

    /**
     * Stores the draft at `kind`. Saving for the user also clears the device
     * override passed in `alsoClearDevices`, so "all my devices" means all.
     */
    async save(kind: ViewScopeKind, alsoClearDevices: string[] = []) {
      const scope = store.scopeFor(kind);
      if (!scope || !store.draft) return;
      await repository.save(view, scope, { ...store.draft } as Record<string, unknown>);
      const { userId } = context();
      if (kind !== 'device' && userId) {
        await Promise.all(
          alsoClearDevices.map((deviceId) =>
            repository.clear(view, { kind: 'device', userId, deviceId })
          )
        );
      }
      await store.load();
    },

    /**
     * Stores only the fields in `patch` at `kind`, keeping the rest of that
     * layer, so a quick toggle does not pin every other field there. The view
     * shows the change before storage answers.
     */
    async setFields(kind: ViewScopeKind, patch: Partial<T>) {
      const scope = store.scopeFor(kind);
      if (!scope) return;
      store.resolved = { ...store.resolved, ...patch };
      const layer = (await repository.load(view, scope).catch(() => null)) ?? {};
      await repository.save(view, scope, { ...layer, ...patch } as Record<string, unknown>);
      await store.load();
    },

    /** Removes the layer at `kind`, so the next one up applies again. */
    async clear(kind: ViewScopeKind) {
      const scope = store.scopeFor(kind);
      if (!scope) return;
      await repository.clear(view, scope);
      await store.load();
    },
  };

  return makeAutoObservable(store, { scopesInOrder: false, scopeFor: false }, { autoBind: true });
}

export type ViewSettingsStore<T extends object> = ReturnType<typeof createViewSettingsStore<T>>;
